import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Calendar, 
  Clock,
  User
} from 'lucide-react';
import { getUpcomingEvents, createEvent, updateEvent, deleteEvent, getAllUsers } from '../services/api';
import { Event, User as UserType } from '../types';
import { HDate, gematriya } from '@hebcal/core';

const EventsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px 12px 45px;
  border: 2px solid #e1e5e9;
  border-radius: 25px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  size: 20px;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const EventCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const EventDate = styled.div`
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
`;

const EventType = styled.div<{ type: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.type) {
      case 'bar_mitzvah': return '#e3f2fd';
      case 'bat_mitzvah': return '#f3e5f5';
      case 'wedding': return '#e8f5e8';
      case 'memorial': return '#ffebee';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'bar_mitzvah': return '#1976d2';
      case 'bat_mitzvah': return '#7b1fa2';
      case 'wedding': return '#2e7d32';
      case 'memorial': return '#d32f2f';
      default: return '#666';
    }
  }};
  display: inline-block;
`;

const EventDescription = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 10px;
`;

const EventActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant: 'edit' | 'delete' }>`
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'edit' ? `
    background: #e3f2fd;
    color: #1976d2;
    
    &:hover {
      background: #bbdefb;
    }
  ` : `
    background: #ffebee;
    color: #d32f2f;
    
    &:hover {
      background: #ffcdd2;
    }
  `}
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [members, setMembers] = useState<UserType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventType: 'other' as 'bar_mitzvah' | 'bat_mitzvah' | 'wedding' | 'memorial' | 'other',
    memberId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const filtered = events.filter(event =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEvents(filtered);
  }, [events, searchTerm]);

  const loadData = async () => {
    try {
      const [eventsData, membersData] = await Promise.all([
        getUpcomingEvents(50), // Get more events for management
        getAllUsers()
      ]);
      
      setEvents(eventsData);
      setMembers(membersData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const getMemberName = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    return member ? `${member.name} ${member.lastName}` : 'לא ידוע';
  };

  const getEventTypeLabel = (type: string) => {
    const labels = {
      bar_mitzvah: 'בר מצווה',
      bat_mitzvah: 'בת מצווה',
      wedding: 'חתונה',
      memorial: 'אזכרה',
      other: 'אחר'
    };
    return labels[type as keyof typeof labels] || 'אחר';
  };

  const getHebrewDate = (date: any) => {
    try {
      let dateObj;
      if (date && typeof date === 'object' && 'toDate' in date) {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        dateObj = new Date(date);
      }

      const hebrewDate = new HDate(dateObj);
      const isLeap = hebrewDate.isLeapYear();
      const monthNum = hebrewDate.getMonth(); // 1..13 per @hebcal/core

      let monthHeb: string;
      if (isLeap && monthNum === 6) {
        monthHeb = 'אדר א׳';
      } else if (isLeap && monthNum === 7) {
        monthHeb = 'אדר ב׳';
      } else {
        const monthNames = [
          'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר',
          'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול'
        ];
        // For leap years, months after Adar II shift by -1 index when mapping to 12-month array
        const index = isLeap && monthNum > 7 ? monthNum - 2 : monthNum - 1;
        monthHeb = monthNames[index] || '';
      }

      const dayHeb = gematriya(hebrewDate.getDate());
      const yearHeb = gematriya(hebrewDate.getFullYear());

      return `${dayHeb} ב${monthHeb} ה${yearHeb}`;
    } catch (error) {
      console.warn('Error converting to Hebrew date:', error);
      return '';
    }
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      eventDate: '',
      eventType: 'other',
      memberId: ''
    });
    setIsModalOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);

    // Handle eventDate conversion safely
    let eventDateString = '';
    if (event.eventDate) {
      try {
        // Check if it's a Firestore Timestamp
        if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
          eventDateString = event.eventDate.toDate().toISOString().split('T')[0];
        } else if (event.eventDate instanceof Date) {
          eventDateString = event.eventDate.toISOString().split('T')[0];
        } else {
          eventDateString = new Date(event.eventDate).toISOString().split('T')[0];
        }
      } catch (error) {
        console.warn('Error converting eventDate:', error);
        eventDateString = '';
      }
    }

    setFormData({
      title: event.title,
      description: event.description || '',
      eventDate: eventDateString,
      eventType: event.eventType,
      memberId: event.memberId || ''
    });
    setIsModalOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, {
          ...formData,
          eventDate: new Date(formData.eventDate),
          memberId: formData.memberId || undefined
        });
      } else {
        await createEvent({
          ...formData,
          eventDate: new Date(formData.eventDate),
          memberId: formData.memberId || undefined,
          createdBy: 'current-user-id' // This should come from auth context
        });
      }
      await loadData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את האירוע?')) {
      try {
        await deleteEvent(eventId);
        await loadData();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  if (loading) {
    return (
      <EventsContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </EventsContainer>
    );
  }

  return (
    <EventsContainer>
      <Header>
        <Title>
          <Calendar size={28} />
          ניהול אירועים
        </Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="חיפוש אירוע..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon size={20} />
        </SearchBar>
        <AddButton onClick={handleAddEvent}>
          <Plus size={20} />
          הוסף אירוע
        </AddButton>
      </Header>

      {filteredEvents.length === 0 ? (
        <EmptyState>
          <Calendar size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
          <h3>אין אירועים במערכת</h3>
          <p>לחץ על "הוסף אירוע" כדי להתחיל</p>
        </EmptyState>
      ) : (
        <EventsGrid>
          {filteredEvents.map((event) => (
            <EventCard key={event.id}>
              <EventHeader>
                <EventInfo>
                  <EventTitle>{event.title}</EventTitle>
                  <EventDate>
                    <Clock size={16} />
                    {(() => {
                      try {
                        let date;
                        if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
                          date = event.eventDate.toDate();
                        } else if (event.eventDate instanceof Date) {
                          date = event.eventDate;
                        } else {
                          date = new Date(event.eventDate);
                        }
                        const gregorianDate = date.toLocaleDateString('he-IL');
                        const hebrewDate = getHebrewDate(event.eventDate);
                        return hebrewDate ? `${gregorianDate} (${hebrewDate})` : gregorianDate;
                      } catch (error) {
                        console.warn('Error displaying event date:', error);
                        return 'תאריך לא תקין';
                      }
                    })()}
                  </EventDate>
                  <EventType type={event.eventType}>
                    {getEventTypeLabel(event.eventType)}
                  </EventType>
                  {event.memberId && (
                    <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      <User size={16} style={{ marginLeft: '5px' }} />
                      {getMemberName(event.memberId)}
                    </div>
                  )}
                  {event.description && (
                    <EventDescription>{event.description}</EventDescription>
                  )}
                </EventInfo>
                <EventActions>
                  <ActionButton
                    variant="edit"
                    onClick={() => handleEditEvent(event)}
                    title="ערוך"
                  >
                    <Edit size={16} />
                  </ActionButton>
                  <ActionButton
                    variant="delete"
                    onClick={() => handleDeleteEvent(event.id)}
                    title="מחק"
                  >
                    <Trash2 size={16} />
                  </ActionButton>
                </EventActions>
              </EventHeader>
            </EventCard>
          ))}
        </EventsGrid>
      )}

      <Modal isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingEvent ? 'ערוך אירוע' : 'הוסף אירוע חדש'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>כותרת האירוע *</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="תיאור האירוע..."
            />
          </FormGroup>

          <FormGroup>
            <Label>תאריך האירוע *</Label>
            <Input
              type="datetime-local"
              value={formData.eventDate}
              onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>סוג אירוע *</Label>
            <Select
              value={formData.eventType}
              onChange={(e) => setFormData({ ...formData, eventType: e.target.value as any })}
              required
            >
              <option value="bar_mitzvah">בר מצווה</option>
              <option value="bat_mitzvah">בת מצווה</option>
              <option value="wedding">חתונה</option>
              <option value="memorial">אזכרה</option>
              <option value="other">אחר</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>מתפלל קשור</Label>
            <Select
              value={formData.memberId}
              onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}
            >
              <option value="">בחר מתפלל</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name} {member.lastName}
                </option>
              ))}
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSaveEvent}>
              {editingEvent ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </EventsContainer>
  );
};

export default Events;
